import { RequireAuth } from '@/components/require-auth'
import { AgencyService } from '@/services/agency-service'
import { UserService } from '@/services/user-service'
import { ApiError, Auth } from '@/services/auth-service'
import { Fragment, OnDispose, prop } from '@tempots/dom'
import { EphimeralService } from '@/utils/ephimeral'
import { OnboardingInfo } from '@/models/onboarding-info'
import { PdfService } from '@/services/pdf-service'
import { ProgressPanel, ProgressPanelData } from '@/components/progress-panel'
import { NavigationService } from '@tempots/ui'
import { Track } from '@/services/track'

const START_LOAD_TIMEOUT_MS = 30_000

function getData() {
  return (
    EphimeralService.retrieve<OnboardingInfo>('onboarding') ?? {
      type: 'onboarding',
    }
  )
}

function goToProcess(agencyId: string) {
  NavigationService.navigate(`/${agencyId}/workspace`, { replace: true })
}

function goToOnboarding() {
  NavigationService.navigate('/onboarding', { replace: true })
}

function goToAgencyOnboarding(agencyId: string) {
  NavigationService.navigate(`/${agencyId}/onboarding`, { replace: true })
}

function goToSignin() {
  NavigationService.navigate('/signin', { replace: true })
}

export function StartPage() {
  return RequireAuth(
    {
      loading: ProgressPanel({
        value: { title: 'Checking your session ...', showLoader: true },
      }),
    },
    () => {
      const user = Auth.user
      const state = prop<ProgressPanelData>({
        title: 'Loading ...',
        showLoader: true,
      })
      let abortController = new AbortController()
      const isLoading = prop(false) // Guard against concurrent calls (reactive signal)
      let loadAgenciesTimeout: ReturnType<typeof setTimeout> | null = null

      const setErrorState = (params: {
        title: string
        description: string
        allowOnboarding?: boolean
      }) => {
        state.set({
          title: params.title,
          description: params.description,
          showLoader: false,
        })
      }

      const loadUserAgencies = async () => {
        // Prevent concurrent calls
        if (isLoading.value) {
          return
        }

        isLoading.set(true)
        abortController.abort()
        const controller = new AbortController()
        abortController = controller
        let timedOut = false
        const timeoutId = setTimeout(() => {
          timedOut = true
          controller.abort()
        }, START_LOAD_TIMEOUT_MS)
        state.set({ title: 'Loading your agency ...' })
        try {
          const { agencies } = await AgencyService.listUserAgencies({
            abortSignal: controller.signal,
          })
          const preferredAgency = AgencyService.preferredAgency.value

          if (agencies.length > 0) {
            let targetAgencyId: string

            if (
              preferredAgency != null &&
              agencies.some(a => a.id === preferredAgency)
            ) {
              targetAgencyId = preferredAgency
            } else {
              targetAgencyId = agencies[0].id
            }

            // Check if user has an agency profile for this agency
            const profileResponse = await UserService.getAgencyProfile(
              targetAgencyId,
              { abortSignal: controller.signal }
            )
            if (profileResponse.recruiterProfile == null) {
              // try to create a profile automatically
              state.set({ title: 'Creating your profile ...' })
              const data = getData()
              if (data.headerRecruiterName != null) {
                await UserService.updateAgencyProfile(
                  targetAgencyId,
                  {
                    name: data.headerRecruiterName,
                    email: data.headerRecruiterEmail ?? undefined,
                    phone: data.headerRecruiterPhoneNumber ?? undefined,
                    linkedIn: data.headerRecruiterLinkedIn ?? undefined,
                  },
                  { abortSignal: controller.signal }
                )
                state.set({ title: 'Profile created' })
                EphimeralService.consume('onboarding')
                goToProcess(targetAgencyId)
                return
              }
              // Redirect to agency onboarding
              goToAgencyOnboarding(targetAgencyId)
              return
            }

            EphimeralService.consume('onboarding')
            // User has a profile, navigate to process page
            goToProcess(targetAgencyId)
            return
          }

          // Attempt at automatically create an agency and agency membership
          const data = getData()
          if (data.headerAgencyName != null) {
            // Consume ephemeral data BEFORE calling onboarding to prevent re-use
            EphimeralService.consume('onboarding')

            const response = await UserService.onboardToAgency({
              recruiterName: data.headerRecruiterName ?? undefined,
              recruiterEmail: data.headerRecruiterEmail ?? undefined,
              recruiterPhoneNumber:
                data.headerRecruiterPhoneNumber ?? undefined,
              recruiterLinkedIn: data.headerRecruiterLinkedIn ?? undefined,
              agencyName: data.headerAgencyName ?? undefined,
              agencyPhoneNumber: data.headerAgencyPhoneNumber ?? undefined,
              agencyUrl: data.headerAgencyUrl ?? undefined,
              agencyLinkedIn: data.headerAgencyLinkedIn ?? undefined,
              agencyEmail: data.headerAgencyEmail ?? undefined,
              // Fallback mirrors onboarding.ts: the seed design and the
              // customize.borderColor it's later compared against (see
              // isUnconfiguredDesign) must be built from the same color, or
              // the setup wizard never auto-opens for this agency. `||` (not
              // `??`) so an empty string from a cleared color input is
              // covered too. When an admin-uploaded default design exists,
              // the fallback is its authored theme color instead of black.
              agencyBrandColor:
                data.agencyBrandColor ||
                (await PdfService.defaultOnboardingBrandColor()),
              abortSignal: controller.signal,
            })
            goToProcess(response.agencyId)
            return
          }

          // if not, fallback to onboarding
          goToOnboarding()
        } catch (error) {
          console.error('Failed to load user agencies:', error)

          if (controller.signal.aborted && !timedOut) {
            return
          }

          if (error instanceof ApiError && error.status === 401) {
            Auth.signout()
            AgencyService.clearUserAgenciesCache()
            goToSignin()
            return
          }

          // Track unexpected errors
          if (error instanceof ApiError) {
            Track.apiError({
              error_code: error.code,
              error_message: error.message,
            })
          } else {
            Track.clientError({
              error_code: 'START_LOAD_AGENCIES_FAILED',
              error_message:
                error instanceof Error ? error.message : String(error),
            })
          }

          const description = timedOut
            ? 'This is taking longer than expected. Please try again.'
            : error instanceof ApiError
              ? error.message
              : error instanceof Error
                ? error.message
                : String(error)

          setErrorState({
            title: 'Unable to continue',
            description,
            allowOnboarding: true,
          })
        } finally {
          clearTimeout(timeoutId)
          isLoading.set(false) // Reset loading flag
        }
      }

      user.on(currentUser => {
        if (currentUser == null) {
          abortController.abort()
          AgencyService.clearUserAgenciesCache()
          goToSignin()
          return
        }

        // Debounce to prevent rapid-fire calls during onboarding
        if (loadAgenciesTimeout) {
          clearTimeout(loadAgenciesTimeout)
        }

        loadAgenciesTimeout = setTimeout(() => {
          void loadUserAgencies()
          loadAgenciesTimeout = null
        }, 100) // 100ms debounce
      })

      return Fragment(
        OnDispose(() => {
          abortController.abort()
          if (loadAgenciesTimeout) {
            clearTimeout(loadAgenciesTimeout)
          }
        }),
        ProgressPanel({ value: state })
      )
    }
  )
}
